import { qualitySchema } from '@wyn/content';
import { z } from 'zod';
import { attackSlotSchema, fightChoiceSchema, fightResultSchema, fightTurnSchema, gearSlotSchema, loadoutSchema } from './fight-report.ts';
import { actionIdSchema, playerSnapshotSchema } from './schemas.ts';

export * from './fight-report.ts';

const gear = z.object({ itemKey: z.string().min(1).max(60), quality: qualitySchema });

const side = z.object({ id: z.string(), name: z.string(), level: z.number().int(), life: z.number(), maxLife: z.number() });

export const fightViewSchema = z.object({
  id: z.string(),
  status: z.enum(['fighting', 'won', 'lost', 'stalemate']),
  turn: z.number().int(),
  maxTurns: z.number().int(),
  attacker: side,
  defender: side,
  /** What the attacker can swing; `ammo` is shots left for a ranged weapon. */
  weapons: z.array(z.object({ slot: attackSlotSchema, name: z.string(), usable: z.boolean(), ammo: z.number().int().nullable() })),
  log: z.array(fightTurnSchema),
  expiresAt: z.number(),
});
export type FightView = z.infer<typeof fightViewSchema>;

export const fightSettlementSchema = z.object({
  fightId: z.string(),
  result: fightResultSchema,
  choice: fightChoiceSchema.nullable(),
  attacker: z.string(),
  defender: z.string(),
  turns: z.number().int(),
  damageDealt: z.number(),
  damageTaken: z.number(),
  renown: z.number().int(),
  coin: z.number().int(),
  prestige: z.number().int(),
  infirmaryUntil: z.number().nullable(),
  escaped: z.boolean(),
  text: z.string(),
});
export type FightSettlement = z.infer<typeof fightSettlementSchema>;

// POST /v1/fight/attack
export const fightAttackRequestSchema = z.object({ actionId: actionIdSchema, target: z.string().min(1).max(40) });
export type FightAttackRequest = z.infer<typeof fightAttackRequestSchema>;
export const fightAttackResponseSchema = z.object({ player: playerSnapshotSchema, fight: fightViewSchema });

// POST /v1/fight/turn
export const fightTurnRequestSchema = z.object({ actionId: actionIdSchema, fightId: z.string().min(1).max(40), slot: attackSlotSchema });
export type FightTurnRequest = z.infer<typeof fightTurnRequestSchema>;
/** `settlement` and `player` are set when the turn ends the fight in a loss or a stalemate. */
export const fightTurnResponseSchema = z.object({ fight: fightViewSchema, settlement: fightSettlementSchema.nullable(), player: playerSnapshotSchema.nullable() });

// POST /v1/fight/finish — the winner's choice.
export const fightFinishRequestSchema = z.object({ actionId: actionIdSchema, fightId: z.string().min(1).max(40), choice: fightChoiceSchema });
export const fightFinishResponseSchema = z.object({ player: playerSnapshotSchema, settlement: fightSettlementSchema });

// POST /v1/fight/withdraw — break off a fight still going.
export const fightWithdrawRequestSchema = z.object({ actionId: actionIdSchema });
export const fightWithdrawResponseSchema = fightFinishResponseSchema;

// GET /v1/fight/current — the fight in progress, to resume after a reload.
export const fightCurrentResponseSchema = z.object({ fight: fightViewSchema.nullable() });

// POST /v1/player/loadout
export const loadoutRequestSchema = z.object({ actionId: actionIdSchema, slot: gearSlotSchema, item: gear.nullable() });
export const loadoutResponseSchema = z.object({
  player: playerSnapshotSchema,
  outcome: z.object({ slot: gearSlotSchema, item: gear.nullable(), loadout: loadoutSchema }),
});
